import { supabase } from "@/integrations/supabase/client";
import { MenuTag } from "@/types/menu";

/**
 * Fetch all menu tags
 * @returns Promise with array of tags
 */
export const fetchMenuTags = async (): Promise<MenuTag[]> => {
  try {
    const { data, error } = await supabase
      .from("menu_tags")
      .select("*")
      .order("name", { ascending: true });

    if (error) throw error;

    return data.map((tag) => ({
      id: tag.id,
      label: tag.name,
      value: tag.id,
      name: tag.name, // For backward compatibility
    }));
  } catch (error) {
    console.error("Error fetching menu tags:", error);
    return [];
  }
};

/**
 * Create a new tag
 * @param tagName The name of the tag to create
 * @returns The created tag or null if failed
 */
export const createMenuTag = async (
  tagName: string
): Promise<MenuTag | null> => {
  try {
    const { data, error } = await supabase
      .from("menu_tags")
      .insert({ name: tagName })
      .select()
      .single();

    if (error) throw error;

    return {
      id: data.id,
      label: data.name,
      value: data.id,
      name: data.name, // For backward compatibility
    };
  } catch (error) {
    console.error("Error creating menu tag:", error);
    return null;
  }
};

/**
 * Update the tags associated with a menu item
 * @param menuItemId The ID of the menu item
 * @param tagIds Array of tag IDs to associate with the menu item
 * @returns Boolean indicating success or failure
 */
export const updateItemTags = async (
  menuItemId: string,
  tagIds: string[]
): Promise<boolean> => {
  try {
    // First delete existing associations
    const { error: deleteError } = await supabase
      .from("menu_items_tags")
      .delete()
      .eq("menu_item_id", menuItemId);

    if (deleteError) throw deleteError;

    // If no tags to add, we're done
    if (tagIds.length === 0) return true;

    // Create tag associations
    const tagAssociations = tagIds.map((tagId) => ({
      menu_item_id: menuItemId,
      tag_id: tagId,
    }));

    const { error: insertError } = await supabase
      .from("menu_items_tags")
      .insert(tagAssociations);

    if (insertError) throw insertError;

    return true;
  } catch (error) {
    console.error("Error updating menu item tags:", error);
    return false;
  }
};
